import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { getOrgs, createOrg, addOrgAppMapping } from '../../../store/orgs';
import { getCognitoUsers, createCognitoUser } from '../../../store/cognito';
import {
  getDemandResources,
  postDemandOrganization,
  postDemandMembership,
} from '../../../store/demand';
import {
  getSolarResources,
  postSolarOrganization,
  postSolarMembership,
} from '../../../store/solar';
import {
  getManagementOrganizations,
  createManagementOrg,
  createManagementOrgUser,
} from '../../../store/management';
import orchestrateAuthApi from '../../../api/orchestrateAuth';
import { toastr } from '../../../components/CustomToast';

// Per-app role granting for Add Member — lets an org's first (or any) member
// also be given admin/editor/viewer rights in a specific connected app at
// the same time, rather than only Console access. EnergyTracer has no
// admin-dashboard membership thunk yet, so it's excluded here too (matching
// CREATE_ORG_THUNKS below).
const APP_ROLE_CONFIG = {
  demand: {
    label: 'Gridcap',
    roleOptions: [
      { value: 0, label: 'Admin' },
      { value: 1, label: 'Editor' },
      { value: 2, label: 'Viewer' },
    ],
    defaultRole: 0,
  },
  solar: {
    label: 'LightLevel',
    roleOptions: [
      { value: 0, label: 'Admin' },
      { value: 1, label: 'Editor' },
      { value: 2, label: 'Viewer' },
    ],
    defaultRole: 0,
  },
  management: {
    label: 'EventSync',
    roleOptions: [
      { value: 'administrator', label: 'Administrator' },
      { value: 'editor', label: 'Editor' },
      { value: 'viewer', label: 'Viewer' },
    ],
    defaultRole: 'administrator',
  },
};

function grantAppAccess(dispatch, appName, appOrgId, role, email, name) {
  if (appName === 'demand') {
    return dispatch(
      postDemandMembership({ org_id: appOrgId, email, name, role })
    ).unwrap();
  }
  if (appName === 'solar') {
    return dispatch(
      postSolarMembership({ org_id: appOrgId, email, name, role })
    ).unwrap();
  }
  if (appName === 'management') {
    return dispatch(
      createManagementOrgUser({
        orgId: appOrgId,
        email,
        name,
        administrator: role === 'administrator',
        editor: role === 'editor',
      })
    ).unwrap();
  }
  return Promise.resolve();
}

// ─── CreateOrgDialog ──────────────────────────────────────────────────────────

function CreateOrgDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const [name, setName] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await dispatch(createOrg(name.trim()));
    setName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Create Organization</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label='Organization Name'
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!name.trim()}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AddMappingDialog ─────────────────────────────────────────────────────────

const APP_OPTIONS = [
  { value: 'demand', label: 'Gridcap' },
  { value: 'solar', label: 'LightLevel' },
  { value: 'energytracer', label: 'EnergyTracer' },
  { value: 'management', label: 'EventSync' },
];

const APP_LABEL = Object.fromEntries(
  APP_OPTIONS.map((o) => [o.value, o.label])
);

// Apps whose admin-dashboard org-creation is already wired up (EnergyTracer
// has no equivalent thunk today — that app keeps manual-ID-entry only).
//
// solar's Organization.address is a non-nullable UnicodeAttribute
// (default="") — it has no valid "unset" representation, so we send an
// explicit empty string rather than omitting the key (which the backend
// would otherwise receive as a literal None and reject). demand's
// Organization.address is nullable, so omitting it there is fine as-is.
const CREATE_ORG_THUNKS = {
  demand: (name) => postDemandOrganization({ name }),
  solar: (name) => postSolarOrganization({ name, address: '' }),
  management: (name) => createManagementOrg(name),
};

function AddMappingDialog({ open, orgId, onClose }) {
  const dispatch = useDispatch();
  const demandOrgs = useSelector((state) => state.demand.organizations);
  const solarOrgs = useSelector((state) => state.solar.organizations);
  const managementOrgs = useSelector((state) => state.management.organizations);

  const [appName, setAppName] = useState('demand');
  const [mode, setMode] = useState('existing');
  const [appOrgId, setAppOrgId] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [saving, setSaving] = useState(false);

  const appOrgs =
    appName === 'demand'
      ? demandOrgs
      : appName === 'solar'
        ? solarOrgs
        : appName === 'management'
          ? managementOrgs
          : null;
  const hasOrgSelect = appOrgs !== null && appOrgs.length > 0;
  const canCreate = Boolean(CREATE_ORG_THUNKS[appName]);

  const resetFields = () => {
    setMode('existing');
    setAppOrgId('');
    setNewOrgName('');
  };

  const handleAppChange = (e) => {
    setAppName(e.target.value);
    resetFields();
  };

  const handleClose = () => {
    resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let targetOrgId = appOrgId.trim();

      if (mode === 'create') {
        if (!newOrgName.trim()) return;
        // Only the org itself is created here — no downstream app user gets
        // provisioned by this. That still only happens per-person, later,
        // once someone with Console access explicitly grants it.
        //
        // Note: these per-app create thunks catch their own errors and show
        // a toastr, but don't re-throw — so a failed creation still resolves
        // (with an undefined payload) rather than rejecting. `.unwrap()`
        // alone can't tell us it failed; that's what the `!result` check
        // below is for. Don't show a second, misleading error here — the
        // thunk's own toastr already reported the real reason.
        const result = await dispatch(
          CREATE_ORG_THUNKS[appName](newOrgName.trim())
        ).unwrap();
        if (!result) {
          return;
        }
        targetOrgId = result?.newOrg?.org_id;
        if (!targetOrgId) {
          toastr.error({
            title: 'Error',
            message: 'Org was created but no id was returned.',
          });
          return;
        }
      }

      if (!targetOrgId) return;
      await dispatch(
        addOrgAppMapping({ orgId, appName, appOrgId: targetOrgId })
      );
      handleClose();
    } catch (err) {
      console.error('ERROR :: addMapping', err);
      toastr.error({ title: 'Error', message: 'Failed to add mapping' });
    } finally {
      setSaving(false);
    }
  };

  const sortedOrgs = appOrgs
    ? [...appOrgs].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    : [];

  const canSubmit =
    mode === 'create' ? Boolean(newOrgName.trim()) : Boolean(appOrgId.trim());

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add App Mapping</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pt: 2,
          mt: 0,
        }}
      >
        <TextField
          select
          size='small'
          label='App'
          fullWidth
          value={appName}
          onChange={handleAppChange}
          sx={{ mt: 1 }}
        >
          {APP_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {canCreate && (
          <ToggleButtonGroup
            size='small'
            exclusive
            value={mode}
            onChange={(e, value) => value && setMode(value)}
          >
            <ToggleButton value='existing'>Existing Org</ToggleButton>
            <ToggleButton value='create'>Create New</ToggleButton>
          </ToggleButtonGroup>
        )}

        {mode === 'create' ? (
          <TextField
            size='small'
            label={`New ${APP_LABEL[appName]} Organization Name`}
            fullWidth
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          />
        ) : hasOrgSelect ? (
          <TextField
            select
            size='small'
            label='Organization'
            fullWidth
            value={appOrgId}
            onChange={(e) => setAppOrgId(e.target.value)}
          >
            {sortedOrgs.map((org) => (
              <MenuItem key={org.org_id} value={org.org_id}>
                <Box>
                  <Typography variant='body2'>{org.name}</Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {org.org_id}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            size='small'
            label='App Org ID'
            fullWidth
            value={appOrgId}
            onChange={(e) => setAppOrgId(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            helperText={
              appOrgs !== null && appOrgs.length === 0
                ? 'Loading orgs…'
                : undefined
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {saving ? <CircularProgress size={18} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── AddMemberDialog ──────────────────────────────────────────────────────────

function AddMemberDialog({ open, orgId, existingMembers, apps, onClose }) {
  const dispatch = useDispatch();
  const cognitoUsers = useSelector((state) => state.cognito.users);
  const [mode, setMode] = useState('existing');
  const [userSub, setUserSub] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [role, setRole] = useState('member');
  const [appGrants, setAppGrants] = useState({});
  const [saving, setSaving] = useState(false);

  const existingSubs = new Set((existingMembers || []).map((m) => m.user_sub));

  const availableUsers = [...cognitoUsers]
    .filter((u) => u.sub && !existingSubs.has(u.sub))
    .sort((a, b) => (a.email || '').localeCompare(b.email || ''));

  const mappedApps = (apps || []).filter((a) => APP_ROLE_CONFIG[a.app_name]);

  const resetAndClose = () => {
    setMode('existing');
    setUserSub('');
    setNewEmail('');
    setNewName('');
    setRole('member');
    setAppGrants({});
    onClose();
  };

  const toggleAppGrant = (appName, enabled) => {
    setAppGrants((current) => ({
      ...current,
      [appName]: {
        enabled,
        role: current[appName]?.role ?? APP_ROLE_CONFIG[appName].defaultRole,
      },
    }));
  };

  const setAppGrantRole = (appName, roleValue) => {
    setAppGrants((current) => ({
      ...current,
      [appName]: {
        enabled: current[appName]?.enabled ?? false,
        role: roleValue,
      },
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      let sub = userSub;
      let email = newEmail.trim();
      let name = newName.trim();

      if (mode === 'new') {
        // Creates the Cognito identity directly in the shared orchestrate
        // user pool.
        const created = await dispatch(
          createCognitoUser({ email, name })
        ).unwrap();
        sub = created.sub;
        dispatch(getCognitoUsers());
      } else {
        const existing = cognitoUsers.find((u) => u.sub === userSub);
        email = existing?.email || '';
        name = existing?.name || '';
      }

      if (!sub) return;

      // Grant per-app access, independently — each of these already shows
      // its own success/failure toast, so one app failing shouldn't block
      // the others or Console membership itself. This is deliberately not a
      // single atomic transaction across four backends.
      for (const appMapping of mappedApps) {
        const grant = appGrants[appMapping.app_name];
        if (!grant?.enabled) continue;
        try {
          await grantAppAccess(
            dispatch,
            appMapping.app_name,
            appMapping.app_org_id,
            grant.role,
            email,
            name
          );
        } catch {
          // Already reported via the underlying thunk's own toastr.
        }
      }

      await orchestrateAuthApi.orgs.addMember(orgId, sub, role);
      toastr.success({ title: 'Member added' });
      resetAndClose();
    } catch (err) {
      console.error('ERROR :: addMember', err);
      toastr.error({ title: 'Error', message: 'Failed to add member' });
    } finally {
      setSaving(false);
    }
  };

  const canSubmit =
    mode === 'existing'
      ? Boolean(userSub)
      : Boolean(newEmail.trim() && newName.trim());

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add Member</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pt: 2,
          mt: 0,
        }}
      >
        <ToggleButtonGroup
          size='small'
          exclusive
          value={mode}
          onChange={(e, value) => value && setMode(value)}
          sx={{ mt: 1 }}
        >
          <ToggleButton value='existing'>Existing User</ToggleButton>
          <ToggleButton value='new'>New User</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'existing' ? (
          <TextField
            select
            size='small'
            label='User'
            fullWidth
            value={userSub}
            onChange={(e) => setUserSub(e.target.value)}
          >
            {availableUsers.length === 0 ? (
              <MenuItem disabled>No users available</MenuItem>
            ) : (
              availableUsers.map((u) => (
                <MenuItem key={u.sub} value={u.sub}>
                  {u.email || u.sub}
                </MenuItem>
              ))
            )}
          </TextField>
        ) : (
          <>
            <TextField
              size='small'
              label='Name'
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <TextField
              size='small'
              label='Email'
              fullWidth
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              helperText='A temporary password will be emailed to them.'
            />
          </>
        )}

        <TextField
          select
          size='small'
          label='Console Role'
          fullWidth
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <MenuItem value='member'>Member</MenuItem>
          <MenuItem value='admin'>Admin</MenuItem>
        </TextField>

        {mappedApps.length > 0 && (
          <>
            <Divider />
            <Typography variant='subtitle2'>App Access</Typography>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ mt: -1 }}
            >
              Console access alone doesn't grant anything in a connected app —
              check a box below to also give this person a role there.
            </Typography>
            {mappedApps.map((appMapping) => {
              const config = APP_ROLE_CONFIG[appMapping.app_name];
              const grant = appGrants[appMapping.app_name];
              return (
                <Box
                  key={appMapping.app_name}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <FormControlLabel
                    sx={{ minWidth: 160 }}
                    control={
                      <Checkbox
                        checked={grant?.enabled ?? false}
                        onChange={(e) =>
                          toggleAppGrant(appMapping.app_name, e.target.checked)
                        }
                      />
                    }
                    label={config.label}
                  />
                  {grant?.enabled && (
                    <TextField
                      select
                      size='small'
                      label='Role'
                      sx={{ minWidth: 160 }}
                      value={grant?.role ?? config.defaultRole}
                      onChange={(e) =>
                        setAppGrantRole(appMapping.app_name, e.target.value)
                      }
                    >
                      {config.roleOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {saving ? <CircularProgress size={18} /> : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── OrgRow ───────────────────────────────────────────────────────────────────

function OrgRow({ org }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [removingMember, setRemovingMember] = useState(null);
  const [removingMapping, setRemovingMapping] = useState(null);

  const cognitoUsers = useSelector((state) => state.cognito.users);

  const fetchDetail = useCallback(() => {
    setDetailLoading(true);
    orchestrateAuthApi.orgs
      .get(org.org_id)
      .then((res) => setDetail(res.data))
      .catch((err) => console.error('ERROR :: loadOrgDetail', err))
      .finally(() => setDetailLoading(false));
  }, [org.org_id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleRemoveMember = async (userSub) => {
    setRemovingMember(userSub);
    try {
      await orchestrateAuthApi.orgs.removeMember(org.org_id, userSub);
      toastr.success({ title: 'Member removed' });
      fetchDetail();
    } catch (err) {
      console.error('ERROR :: removeMember', err);
      toastr.error({ title: 'Error', message: 'Failed to remove member' });
    } finally {
      setRemovingMember(null);
    }
  };

  // Only unlinks the orchestrate org from the app org — the app org itself,
  // and any memberships in it, are left untouched.
  const handleRemoveMapping = async (mapping) => {
    if (removingMapping) return;
    const label = APP_LABEL[mapping.app_name] || mapping.app_name;
    if (
      !window.confirm(
        `Remove the ${label} mapping (${mapping.app_org_id}) from "${org.name}"?`
      )
    ) {
      return;
    }
    setRemovingMapping(mapping.type);
    try {
      await orchestrateAuthApi.orgs.removeMapping(
        org.org_id,
        mapping.app_name,
        mapping.app_org_id
      );
      toastr.success({ title: 'App mapping removed' });
      fetchDetail();
    } catch (err) {
      console.error('ERROR :: removeMapping', err);
      toastr.error({ title: 'Error', message: 'Failed to remove mapping' });
    } finally {
      setRemovingMapping(null);
    }
  };

  const emailBySub = Object.fromEntries(
    cognitoUsers.filter((u) => u.sub).map((u) => [u.sub, u.email || u.sub])
  );

  const apps = detail?.apps || [];
  const members = detail?.members || [];

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
        onClick={() => setExpanded((v) => !v)}
      >
        <TableCell>
          <IconButton size='small'>
            <FontAwesomeIcon
              icon={['fal', expanded ? 'chevron-up' : 'chevron-down']}
              size='xs'
            />
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant='body2'>{org.name}</Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ fontFamily: 'monospace' }}
          >
            {org.org_id}
          </Typography>
        </TableCell>
        <TableCell align='center'>
          {detailLoading ? <CircularProgress size={12} /> : apps.length}
        </TableCell>
        <TableCell align='center'>
          {detailLoading ? <CircularProgress size={12} /> : members.length}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={4} sx={{ p: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ px: 4, py: 2, bgcolor: 'grey.50' }}>
              {detailLoading ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}
                >
                  <CircularProgress size={16} />
                  <Typography variant='body2' color='text.secondary'>
                    Loading…
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* App Mappings */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>App Mappings</Typography>
                    <Button
                      size='small'
                      startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMappingDialogOpen(true);
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  {apps.length === 0 ? (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ mb: 2 }}
                    >
                      No app mappings yet
                    </Typography>
                  ) : (
                    <Box
                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}
                    >
                      {apps.map((a) => (
                        <Tooltip
                          key={a.type}
                          title={a.app_org_id}
                          placement='top'
                        >
                          <Chip
                            label={APP_LABEL[a.app_name] || a.app_name}
                            size='small'
                            variant='outlined'
                            onDelete={(e) => {
                              e.stopPropagation();
                              handleRemoveMapping(a);
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  )}

                  <Divider sx={{ my: 1.5 }} />

                  {/* Members */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>Members</Typography>
                    <Button
                      size='small'
                      startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMemberDialogOpen(true);
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  {members.length === 0 ? (
                    <Typography variant='body2' color='text.secondary'>
                      No members yet
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      {members.map((m) => (
                        <Box
                          key={m.user_sub}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant='body2' sx={{ flex: 1 }}>
                            {emailBySub[m.user_sub] || m.user_sub}
                          </Typography>
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ minWidth: 48 }}
                          >
                            {m.role || 'member'}
                          </Typography>
                          <IconButton
                            size='small'
                            disabled={removingMember === m.user_sub}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMember(m.user_sub);
                            }}
                          >
                            {removingMember === m.user_sub ? (
                              <CircularProgress size={14} />
                            ) : (
                              <FontAwesomeIcon
                                icon={['fal', 'trash']}
                                size='xs'
                              />
                            )}
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <AddMappingDialog
        open={mappingDialogOpen}
        orgId={org.org_id}
        onClose={() => {
          setMappingDialogOpen(false);
          fetchDetail();
        }}
      />
      <AddMemberDialog
        open={memberDialogOpen}
        orgId={org.org_id}
        existingMembers={members}
        apps={apps}
        onClose={() => {
          setMemberDialogOpen(false);
          fetchDetail();
        }}
      />
    </>
  );
}

// ─── Organizations ────────────────────────────────────────────────────────────

export default function Organizations() {
  const dispatch = useDispatch();
  const { orgs } = useSelector((state) => state.orgs);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(getOrgs());
    dispatch(getCognitoUsers());
    dispatch(getDemandResources());
    dispatch(getSolarResources());
    dispatch(getManagementOrganizations());
  }, [dispatch]);

  const filteredOrgs = useMemo(() => {
    const sorted = [...orgs].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
    if (!search.trim()) return sorted;
    const q = search.trim().toLowerCase();
    return sorted.filter(
      (o) =>
        (o.name || '').toLowerCase().includes(q) ||
        (o.org_id || '').toLowerCase().includes(q)
    );
  }, [orgs, search]);

  const paginatedOrgs = filteredOrgs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Grid container justifyContent='center' sx={{ mt: 1 }}>
      <Grid size={{ xs: 12 }} sx={{ p: '20px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography variant='h6'>Organizations</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size='small'
              placeholder='Search…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 220 }}
            />
            <Button
              variant='contained'
              startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
              onClick={() => setCreateDialogOpen(true)}
            >
              New Org
            </Button>
          </Box>
        </Box>
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={40} />
                <TableCell>Name</TableCell>
                <TableCell align='center' width={60}>
                  Apps
                </TableCell>
                <TableCell align='center' width={80}>
                  Members
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align='center'
                    sx={{ color: 'text.secondary', py: 4 }}
                  >
                    {orgs.length === 0 ? 'No organizations yet' : 'No results'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedOrgs.map((org) => (
                  <OrgRow key={org.org_id} org={org} />
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component='div'
            count={filteredOrgs.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            RowsPerPageChange={[10]}
          />
        </TableContainer>
      </Grid>
      <CreateOrgDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />
    </Grid>
  );
}
